import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { isEqual } from 'underscore';
import { cloneDeep } from 'lodash';

import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { IncludeOrExclude } from '../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { JournodexEnumsService } from '@Discover/Core/services/journodex-enums.service';
import { MediaMarket } from '@Discover/Core/models/media-market.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { mediaMarketTypeToDisplayStringMap, MediaMarketTypeEnum } from '@api/journodex/enum/media-market-type.enum';

export interface BrowseAllType {
    includeOrExclude: IncludeOrExclude;
    parentType?: MediaMarketTypeEnum;
    childId?: number;
}

@Component({
    selector: 'agpr-include-exclude-media-market-chips',
    templateUrl: './include-exclude-media-market-chips.component.html',
    styleUrls: [
        './include-exclude-media-market-chips.component.scss',
        '../../../../Common/Forms/components/IncludeExcludeChips/include.exclude.chips.component.scss'
    ],
    providers: [getProvider(IncludeExcludeMediaMarketChipsComponent)],
    standalone: false,
    animations: [
        trigger('containerState', [
            state('hidden', style({
                opacity: 0,
                height: 0
            })),
            state('visible', style({
                opacity: 1,
                height: '*'
            })),
            transition('visible <=> hidden', animate('250ms ease-in'))
        ]),
        trigger('labelState', [
            state('visible', style({
                opacity: 1,
                flex: 1,
                width: '45px',
                minWidth: '45px',
                maxWidth: '45px'
            })),
            state('hidden', style({
                opacity: 0,
                flex: 0,
                width: 0
            })),
            transition('hidden <=> visible', animate('250ms ease-in'))
        ])
    ]
})
export class IncludeExcludeMediaMarketChipsComponent extends ControlValueAccessorModel<IncludeExcludeModel<MediaMarket>>
    implements OnInit {

    @Input() id: string = null;
    @Input() disabled: boolean = false;

    @Output() browseAll = new EventEmitter<BrowseAllType>();

    showBrowseAll: boolean;
    expanded: boolean = false;

    readonly includeOrExclude = IncludeOrExclude;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly mediaMarketGroupMap = mediaMarketTypeToDisplayStringMap;

    constructor(
        public journodexEnumsService: JournodexEnumsService,
    ) {
        super();
    }

    ngOnInit() {
        if (!this.value) {
            this.value = new IncludeExcludeModel<MediaMarket>();
        } else {
            this.checkShouldExpand();
        }

        this.showBrowseAll = this.browseAll.observers.length > 0;
    }

    writeValue(value: any) {
        super.writeValue(value);
        this.checkShouldExpand();
    }

    handleChanges(includeExclude: IncludeOrExclude, value: MediaMarket[]) {
        if (this.value && this.value[includeExclude] && !isEqual(this.value[includeExclude], value)) {
            this.value[includeExclude] = cloneDeep(value);
            this.onModelChange(this.value);
        }

        this.checkShouldExpand();
    }

    addChip(mediaMarket: MediaMarket, includeExclude: IncludeOrExclude) {
        if (this.value[includeExclude].find(x => x.id === mediaMarket.id)) {
            return;
        }

        this.value[includeExclude].push(mediaMarket);
        this.value[includeExclude] = cloneDeep(this.value[includeExclude]);

        this.onModelChange(this.value);
        this.checkShouldExpand();
    }

    /**
     * Removes the entire media market group, iterate reversely when removing inplace
     * @param groupType - type that should be removed
     * @param mediaMarkets - selected mediaMarkets
     */
    removeGroup(
        groupType: MediaMarketTypeEnum,
        includeExclude: IncludeOrExclude,
        mediaMarkets: MediaMarket[]
    ) {
        for (let i = mediaMarkets.length - 1; i >= 0; i--) {
            if (mediaMarkets[i].type === groupType) {
                mediaMarkets.splice(i, 1);
            }
        }

        this.value[includeExclude] = cloneDeep(mediaMarkets);

        this.onModelChange(this.value);
        this.checkShouldExpand();
    }

    checkShouldExpand() {
        if (this.value && (this.value.exclude.length > 0 || this.value.includeAll?.length > 0)) {
            this.expanded = true;
        }
    }

    toggleExpanded() {
        // Closes any panels that are open before toggling
        document.body.click();
        this.expanded = !this.expanded;
    }
}
